import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { queuePropType, readerRefPropType } from "utils/prop-types";
import { useTranslation } from "react-i18next";
import { searchDocDomSeek } from "@d-i-t-a/reader/dist/esm/index";
import { searchEbook } from "api/ebook";
import { useTheme } from "@material-ui/core/styles";
import { READER_THEMES } from "utils/themes";
import { getMediaKey } from "api/media";
import { extractTransformersUidFromMediaKey } from "api/media/media.utils";
import EbookSearchMenu from "components/ebook-reader/components/search-menu";
import { SearchTypes } from "components/ebook-reader/components/search-menu/search-menu.constants";


const MIN_LENGTH_TO_SEARCH = 3;

const ContentStatus = Object.freeze({
    default: 'empty',
    content: 'content',
    noResults: 'no results',
});

const SearchMenu = memo((props) => {
    const { activeTheme, queue, activeIndex, controlBtnProps, readerRef } = props;
    const { downloadUrl } = queue[activeIndex];
    const theme = useTheme();
    const { t } = useTranslation();

    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isFixedLayout, setFixedLayout] = useState(true);
    const [searchResults, setSearchResults] = useState([]);
    const [searchType, setSearchType] = useState(SearchTypes.chapter);
    const [contentStatus, setContentStatus] = useState(ContentStatus.default);
    const [bookUUID, setBookUUID] = useState();

    useEffect(() => {
        const readerLayout = readerRef.current?.navigator?.publication?.isFixedLayout;
        if (readerLayout != null && readerLayout !== isFixedLayout) {
            setFixedLayout(readerLayout);
        }
    }, [readerRef.current]);

    useEffect(() => {
        const defaultType = isFixedLayout ? SearchTypes.book : SearchTypes.chapter;
        setSearchType(defaultType);
    }, [isFixedLayout]);

    useEffect(() => {
        getMediaKey(downloadUrl).then(({ key }) => {
            const uuid = extractTransformersUidFromMediaKey(key);
            setBookUUID(uuid);
        })
    }, [setBookUUID]);

    const messages = useMemo(() => ({
        [ContentStatus.default]: {
            title: t("ebook_search_results_empty_title"),
            message: t("ebook_search_results_empty_message"),
        },
        [ContentStatus.noResults]: {
            title: t("ebook_search_no_results_title"),
            message: t("ebook_search_no_results_message"),
        },
    }), []);

    /** disable search for too small terms, global search with no uuid, or local search in fixed layout **/
    const isSearchEnabled = useMemo(
        () =>
            inputValue.length >= MIN_LENGTH_TO_SEARCH &&
            (searchType === SearchTypes.book ? !!bookUUID : true) &&
            (searchType === SearchTypes.chapter ? !isFixedLayout : true)
        ,
        [inputValue, searchType, bookUUID]
    );

    /** change search type **/
    const onSearchTypeChange = useCallback((_, value) => {
        setSearchType(value);
        setSearchResults([]);
        setContentStatus(ContentStatus.default);
    }, []);

    /** keep input value state **/
    const handleInputChange = useCallback(
    (event) => {
        const value = event.currentTarget.value;
        setInputValue(value);
        if (value === "") {
            afterInputCleared();
        }
    }, [readerRef.current]);

    /** clear input **/
    const afterInputCleared = useCallback(() => {
        setInputValue("");
        setIsLoading(false);
        setSearchResults([]);
        setContentStatus(ContentStatus.default);
    }, []);

    /**
     * Memoize the uuids of the search results in the order they come in
     * (assumed to be the order in which they come up in the text),
     * and group them by resource `href`,
     * so that we can assign the same ids to the results we find when looking
     * for navigation info in the local chapter.
     * @type {Object.<string, string[]>}
     */
    const uuidsByHref = useMemo(() => {
        const groupedUuids = {};
        searchResults.forEach((item) => {
           groupedUuids[item.href] = groupedUuids[item.href] ?? [];
           groupedUuids[item.href].push(item.uuid);
        });
        return groupedUuids;
    }, [searchResults]);

    /**
     * Cache locations of our results as we find them in the front-end.
     * Store by chapter
     * @type {Object.<string, Object[]>}
     */
    const cachedLocations = useMemo(() => ({}),[inputValue, searchType]);
    const getCachedLocations = useCallback(( chapterHref ) => cachedLocations[chapterHref], [cachedLocations]);
    const setCachedLocations = useCallback(( chapterHref, results ) => {
        cachedLocations[chapterHref] = [...results];
    }, [cachedLocations]);

    /** find and cache search term locations in the current chapter **/
    const findAndCacheTermLocations = useCallback(async (result) => {
        const { href, textMatch: term, title } = result;
        const localSearchResults = [];
        const navigator = readerRef.current.navigator;
        const currentHref = navigator.currentLocator().href;
        const absoluteUrl = navigator.publication.getAbsoluteHref(href);

        const doc = (
            (currentHref && currentHref === href) ?
                // local chapter already stored
                navigator.view.iframe.contentDocument :
                // load chapter
                await navigator.api.getContent(absoluteUrl).then(async (content) => {
                    const parser = new DOMParser();
                    return parser.parseFromString(content, "application/xhtml+xml")
                })
        );

        await searchDocDomSeek(term, doc, href, title).then((results) => {
            results.forEach((searchItem, idx) => {
                searchItem.uuid = uuidsByHref[href][idx];
                localSearchResults.push(searchItem);
            });
        });

        setCachedLocations(href, localSearchResults);
        return localSearchResults;
    }, [readerRef.current, uuidsByHref, cachedLocations]);

    /** load and navigate to the chapter, if we're not there already **/
    const maybeGoToChapter = useCallback(({ href }) => {
        const navigator = readerRef.current.navigator;
        const currentLocation = navigator.currentChapterLink.href;
        const absoluteUrl = navigator.publication.getAbsoluteHref(href);
        if (currentLocation !== absoluteUrl) {
            return navigator.navigate({ href: absoluteUrl, locations: { position: 0 }, title: "title" });
        }
        return Promise.resolve();
    }, [readerRef.current]);

    /** go to the search result's position **/
    const goToResult = useCallback(({ rangeInfo }) => {
        const navigator = readerRef.current.navigator;
        navigator.view?.goToCssSelector(rangeInfo.startContainerElementCssSelector);
        navigator.updatePositionInfo();
    }, [readerRef.current]);

    /** highlight the search result's position **/
    const highlightResult = useCallback((result) => {
        const searchModule = readerRef.current.searchModule;
        const searchHighlightType = 1;
        searchModule.highlighter?.destroyHighlights(searchHighlightType);
        searchModule.createSearchHighlight(result, theme.palette.colors.readerSearchHighlightColor);
    }, [readerRef.current]);

    /**
     * Search in the result's resource and find the result's position to navigate to.
     * We build a cache of results locations as we find them
     * **/
    const onResultClick = useCallback((result) => async () => {
        const { uuid, href: chapterHref } = result;

        const cache = getCachedLocations(chapterHref);
        const localSearchResults = cache || await findAndCacheTermLocations(result);

        const item = localSearchResults.filter(res => res.uuid === uuid)[0];

        maybeGoToChapter(item).then(() => {
            setTimeout(() => {
                goToResult(item);
                highlightResult(item);
            }, 300);
        });
    }, [searchResults, readerRef.current, cachedLocations, uuidsByHref]);

    /**
     * Search in local chapter
     * Also caches the locations for navigation so they are not recomputed on result click
     * **/
    const localSearch = useCallback(() => {
        const navigator = readerRef.current.navigator;
        const { href } = navigator.currentLocator();
        return new Promise(async (resolve) => {
            const { title } = navigator.currentChapterLink;
            const doc = navigator.view.iframe.contentDocument;
            const localResults = await searchDocDomSeek(inputValue, doc, href, title);
            setCachedLocations(href, localResults);
            resolve(localResults);
        });
    }, [inputValue, cachedLocations, readerRef.current]);

    /** search **/
    const handleSearch = useCallback( async (e) => {
        e.preventDefault();
        setContentStatus(ContentStatus.content);
        setSearchResults([]);
        if (!isSearchEnabled) {
            setContentStatus(ContentStatus.default);
            return;
        }

        setIsLoading(true);
        const performSearch = (
            searchType === SearchTypes.chapter ?
                localSearch :
                () => searchEbook(bookUUID, inputValue)
        );

        performSearch()
        .then(results => {
            setIsLoading(false);
            setSearchResults(results);
            if (results && results.length === 0) {
                setContentStatus(ContentStatus.noResults);
            }
        })
        .catch(() => {
            setIsLoading(false);
            setSearchResults([]);
            setContentStatus(ContentStatus.noResults);
        });
    }, [inputValue, searchType, readerRef, findAndCacheTermLocations, isSearchEnabled, bookUUID]);

    return (
        <EbookSearchMenu
            activeTheme={activeTheme}
            controlBtnProps={controlBtnProps}
            showSearchMenu={readerRef.current?.navigator?.rights?.enableSearch}
            showSearchTypeTabs={!isFixedLayout}
            searchButtonEnabled={isSearchEnabled}
            globalSearchEnabled={!!bookUUID}
            handleSearch={handleSearch}
            onResultClick={onResultClick}
            onSearchTypeChange={onSearchTypeChange}
            afterInputCleared={afterInputCleared}
            onInputChange={handleInputChange}
            searchResults={searchResults}
            isLoading={isLoading}
            contentStatus={contentStatus}
            inputValue={inputValue}
            searchType={searchType}
            message={messages[contentStatus]}
        />
    )
});

SearchMenu.propTypes = {
    activeTheme: PropTypes.oneOf(Object.keys(READER_THEMES)),
    queue: queuePropType,
    activeIndex: PropTypes.number,
    readerRef: readerRefPropType,
    controlBtnProps: PropTypes.shape({
        className: PropTypes.string,
        iconClassName: PropTypes.string,
    }),
};

export default SearchMenu;
